import Phaser from 'phaser';
import type { HeroClass } from '@roe/shared';
import { armoryFrameTier, getPassivesForHero } from '@roe/shared';
import { drawHeroFrameOrnament, drawTitleFlourish } from '../utils/heroFrameOrnament';
import { GAME_W, GAME_H, setupCamera } from '../dpr.js';
import { GameStateManager } from '../state/GameStateManager';
import { MenuScene } from './MenuScene';
import { createHudTooltip } from '../utils/tooltip';
import { LOADED_ASSET_KEYS } from './BootScene.js';

interface SpellInfo {
  short: string;
  name: string;
  desc: string;
  effectType: string;
  iconKey?: string;
}

const ABILITY_COLORS: Record<string, number> = {
  aoe: 0xef4444,
  dot: 0x22c55e,
  hot: 0x22c55e,
  slow: 0x3b82f6,
  heal: 0x10b981,
  buff: 0xfbbf24,
  stun: 0xeab308,
  fear: 0x4090c0,
  resurrect: 0xcc3322,
  passive_resurrect: 0xef4444,
  passive_loot: 0xfbbf24,
  stone_form: 0x94a3b8,
  thunder_strike: 0xfbbf24,
  mace_throw: 0xd97706,
  damage: 0xf97316,
  bear_form: 0x86efac,
};

interface HeroOption {
  heroClass: HeroClass;
  /** Optional sprite/ability key override — used when multiple heroes share the same class */
  heroKey?: string;
  name: string;
  classLabel: string;
  description: string;
  color: number;
  spells: SpellInfo[];
  /** When true, hero is hidden from Choose Your Hero + Hero Armory (e.g. WIP
   *  characters waiting on better art / kit work). Roster definition stays
   *  intact so anything that imports the entry by index still works. */
  hidden?: boolean;
}

/** Heroes the player can actually pick / browse — `HERO_OPTIONS` filtered by
 *  the `hidden` flag. Use this list in scenes; `HERO_OPTIONS` is the master. */
export const VISIBLE_HERO_OPTIONS = (): HeroOption[] => HERO_OPTIONS.filter((h) => !h.hidden);

export const HERO_OPTIONS: HeroOption[] = [
  {
    heroClass: 'warrior', name: 'Goomar', classLabel: 'Warrior', color: 0xf59e0b,
    description: 'Rage-fueled melee fighter\nCharges the front line. Weak at range.',
    spells: [
      { short: 'WHI', name: 'Whirlwind',     desc: 'Spins into a deadly storm — leaves all nearby bleeding', effectType: 'aoe',   iconKey: 'spell_whirlwind' },
      { short: 'EAR', name: 'Earthquake',    desc: 'Shakes the earth itself — stuns all enemies and leaves them vulnerable', effectType: 'stun', iconKey: 'spell_earthquake' },
      { short: 'BRK', name: 'Berserk',       desc: 'Flies into savage rage — strikes harder and faster',  effectType: 'berserk',          iconKey: 'spell_berserk' },
      { short: 'UDF', name: 'Undying Fury',  desc: 'Rises from near death, fueled with lethal fury', effectType: 'passive_resurrect',iconKey: 'spell_undying_fury' },
    ],
  },
  {
    heroClass: 'tank', name: 'Ironheld', classLabel: 'Tank', color: 0x3b6eb0,
    description: 'Holy tank, shields & reflects',
    spells: [
      { short: 'CHA', name: 'Challenge',      desc: 'Draws enemy wrath — forces a foe to face him alone',  effectType: 'taunt',   iconKey: 'spell_challenge' },
      { short: 'SHI', name: 'Holy Shield',    desc: 'Raises a sacred barrier to protect a wounded ally',     effectType: 'buff',     iconKey: 'spell_shield' },
      { short: 'CON', name: 'Consecration',   desc: 'Sacred fire scorches all who dare approach',             effectType: 'aoe',      iconKey: 'spell_consecration' },
      { short: 'DIV', name: 'Divine Reflect', desc: 'Turns their own steel against them',                     effectType: 'buff',     iconKey: 'spell_divine_reflect' },
    ],
  },
  {
    heroClass: 'wizard', name: 'Zyphara', classLabel: 'Wizard', color: 0x4090c0,
    description: 'Fire wizard — burns everything',
    spells: [
      { short: 'FIR', name: 'Fireball',     desc: 'A searing bolt of flame that leaves enemies burning',   effectType: 'fireball',    iconKey: 'spell_fireball' },
      { short: 'ERP', name: 'Eruption',     desc: 'The ground erupts — fire swallows the whole battlefield', effectType: 'eruption',    iconKey: 'spell_eruption' },
      { short: 'ARC', name: 'Arcane Might', desc: 'Restores mana to all allies and grants +5% resist all for 10s', effectType: 'buff', iconKey: 'spell_arcane_might' },
      { short: 'MAG', name: 'Magma Shield', desc: 'A shell of molten rock — attackers recoil in pain',       effectType: 'magma_shield',iconKey: 'spell_magma_shield' },
    ],
  },
  {
    heroClass: 'archer', name: 'Sylvari', classLabel: 'Ranger', color: 0x22c55e,
    description: 'Fast ranged attacker',
    spells: [
      { short: 'MUL', name: 'Multishot',    desc: 'Looses a hail of arrows across the enemy line',         effectType: 'aoe',    iconKey: 'spell_multishot' },
      { short: 'TRP', name: 'Poison Trap',  desc: 'A venomous trap snaps shut on the 3 frontmost enemies — roots and poisons them', effectType: 'dot', iconKey: 'spell_poison_trap' },
      { short: 'PIE', name: 'Piercing Shot',desc: 'One devastating shot — obliterates a single target and shreds their armor', effectType: 'damage', iconKey: 'spell_piercing_shot' },
      { short: 'EAG', name: 'Eagle Eye',    desc: 'Sharpens focus to a razor edge — every shot finds its mark', effectType: 'buff', iconKey: 'spell_eagle_eye' },
    ],
  },
  {
    heroClass: 'healer', name: 'Lumiel', classLabel: 'Healer', color: 0xf472b6,
    description: 'Heals, fears & resurrects',
    spells: [
      { short: 'FEA', name: 'Fear',         desc: 'A soul-rending shriek that shatters enemy will',         effectType: 'fear',      iconKey: 'spell_fear' },
      { short: 'WOD', name: 'Word of Death',desc: 'A whispered curse that slowly drains the life within',    effectType: 'dot',       iconKey: 'spell_word_of_death' },
      { short: 'RES', name: 'Resurrection', desc: 'Calls a fallen ally back from the brink of death',        effectType: 'resurrect', iconKey: 'spell_resurrection' },
      { short: 'BLE', name: 'Bless',        desc: 'A gentle blessing that mends wounds over time',           effectType: 'heal',      iconKey: 'spell_bless' },
    ],
  },
  {
    heroClass: 'assassin', name: 'Kael', classLabel: 'Assassin', color: 0x64748b,
    description: 'Energy-based poison assassin.\nCombo Points: every 5 hits on the same target trigger a bonus attack.',
    spells: [
      { short: 'SIN', name: 'Sinister Strike',  desc: 'A vicious cut laced with arcane poison',                  effectType: 'dot',        iconKey: 'spell_sinister_strike' },
      { short: 'DBL', name: 'Dirty Blades',     desc: 'Vanishes into shadow and strikes from behind, unseen',     effectType: 'shadow_step', iconKey: 'spell_poison_cloud' },
      { short: 'SHU', name: 'Shuriken Barrage', desc: 'A storm of blades tears through enemies in rapid succession', effectType: 'damage',   iconKey: 'spell_shuriken_barrage' },
      { short: 'EVA', name: 'Evasion',          desc: 'Becomes a blur — most strikes pass harmlessly through air', effectType: 'buff',        iconKey: 'spell_evasion' },
    ],
  },
  {
    heroClass: 'archer', heroKey: 'amazon', name: 'Zayla', classLabel: 'Ranger', color: 0xf472b6,
    description: 'Ranged hunter — leaps into melee, launches javelins',
    spells: [
      { short: 'LEP', name: 'Leap Strike',    desc: 'Launches into the air and crashes down on target — armor means nothing', effectType: 'jump_strike',    iconKey: 'spell_leap_strike' },
      { short: 'FRZ', name: 'Freezing Shot',  desc: 'A frost arrow that locks the target in ice — utterly helpless',          effectType: 'freeze',         iconKey: 'spell_freezing_shot' },
      { short: 'WAR', name: 'War Cry',        desc: 'A battle cry that surges all allies with fury and swiftness',             effectType: 'war_cry',        iconKey: 'spell_war_cry' },
      { short: 'SPR', name: 'Spear Volley (passive)', desc: 'Every tenth strike becomes a devastating javelin throw',            effectType: 'passive_spear',  iconKey: 'spell_spear_volley' },
    ],
  },
  {
    heroClass: 'warrior', heroKey: 'dwarf', name: 'Lootfi', classLabel: 'Warrior', color: 0xd97706,
    description: 'Hard-hitting dwarf warrior with guaranteed crits & lucky loot\nCharges the front line. Weak at range.',
    spells: [
      { short: 'LCK', name: 'Lucky Loot',      desc: 'His keen eye never misses a coin on the ground', effectType: 'passive_loot',    iconKey: 'spell_lucky_loot' },
      { short: 'STO', name: 'Stone Form',       desc: 'Turns to living stone — nearly impossible to harm',       effectType: 'stone_form',      iconKey: 'spell_stone_form' },
      { short: 'THU', name: 'Thunder Strike',   desc: 'Calls down thunder on a single foe — always a killing blow', effectType: 'thunder_strike', iconKey: 'spell_thunder_strike' },
      { short: 'MAC', name: 'Mace Throw',       desc: 'Hurls his mace through the air — dazes all in its path',  effectType: 'mace_throw',      iconKey: 'spell_mace_throw' },
    ],
  },
  {
    heroClass: 'warrior', heroKey: 'valkyrie', name: 'Valka', classLabel: 'Valkyrie', color: 0xc084fc,
    description: 'A scarred warchief of the plains tribes.\nWhere Valka plants her banner, the line holds — where she swings, it breaks.',
    spells: [
      { short: 'SBH', name: 'Shield Bash',       desc: 'Charge forward — slow + absorb shield on impact',                         effectType: 'shield_bash_charge',  iconKey: 'spell_valka_shield_bash' },
      { short: 'TMP', name: 'Tempest Slam', desc: 'Spin around her, then slam a front-cone finisher scaling with mobs hit', effectType: 'whirlwind_bludgeon', iconKey: 'spell_valka_whirlwind' },
      { short: 'BAN', name: 'Banner of Valor',   desc: 'Plant a rallying banner — nearby allies gain an attack-speed surge',      effectType: 'plant_banner',        iconKey: 'spell_valka_banner' },
      { short: 'CTR', name: 'Counter-Strike (passive)', desc: '30% chance on melee hit to retaliate with an 80% ATK strike',      effectType: 'passive_counter',     iconKey: 'spell_valka_counter' },
    ],
  },
  {
    heroClass: 'wizard', heroKey: 'necromancer', name: 'Morveth', classLabel: 'Necromancer', color: 0x7c3aed,
    description: 'Dark wizard — raises undead, drains souls, transforms into a Lich',
    spells: [
      { short: 'DCL', name: 'Death Coil',  desc: 'Hurls a coil of death energy — instant damage and lingering plague', effectType: 'death_coil',  iconKey: 'spell_death_coil' },
      { short: 'RDT', name: 'Raise Dead',  desc: 'Raises the nearest corpse as a skeletal servant to fight for you',   effectType: 'raise_dead',  iconKey: 'spell_raise_dead' },
      { short: 'SLC', name: 'Soul Leech',  desc: 'Drains life force from each kill — heals Morveth and stores souls',  effectType: 'soul_leech',  iconKey: 'spell_soul_leech' },
      { short: 'BNS', name: 'Bone Shield', desc: 'Erects a barrier of compressed bone — absorbs incoming damage',      effectType: 'bone_shield', iconKey: 'spell_bone_shield' },
    ],
  },
  {
    heroClass: 'wizard', heroKey: 'sorcerer', name: 'Cyrith', classLabel: 'Sorcerer', color: 0x80c8ff,
    description: 'Stormbound — sword in one hand, lightning in the other. She walks the line behind Vorrak, the sentinel of her own forging, and the storm answers when she calls.',
    spells: [
      { short: 'GOL', name: 'Forge Vorrak',  desc: 'Calls forth Vorrak — a tireless storm-forged guardian, hard to break, slow to fall',          effectType: 'summon_copper_golem', iconKey: 'spell_summon_golem' },
      { short: 'SBT', name: 'Storm Bolt',    desc: 'Looses a kite of crackling storm at the nearest foe',                                          effectType: 'storm_bolt',          iconKey: 'spell_storm_bolt' },
      { short: 'STA', name: 'Static Field',  desc: 'Wraps Vorrak in a humming aura — every foe that steps within shudders as lightning crawls in', effectType: 'static_field',        iconKey: 'spell_static_field' },
      { short: 'ARC', name: 'Arc Strike',    desc: 'A blade-sweep wreathed in lightning — leaps onto the nearest foe and chains to a second. Only fires from the front rank.', effectType: 'arc_strike', iconKey: 'spell_arc_strike' },
    ],
  },
  {
    heroClass: 'druid', name: 'Thorne', classLabel: 'Druid', color: 0x4ade80,
    description: 'Hybrid nature caster — build damage, healing, or both through talents',
    spells: [
      { short: 'THR', name: 'Thornlash',    desc: 'Whip of thorns — single-target damage + bleed. Full talent scaling for damage spec',  effectType: 'dot',        iconKey: 'spell_thornlash' },
      { short: 'REG', name: 'Regrowth',     desc: 'Verdant heal over time — talent adds more ally targets',                               effectType: 'hot',        iconKey: 'spell_regrowth' },
      { short: 'WLG', name: 'Wild Growth',  desc: 'Thorn roots burst around Thorne, damaging + slowing all nearby enemies',               effectType: 'aoe',        iconKey: 'spell_wild_growth' },
      { short: 'NAT', name: 'Nature Form',  desc: 'Shift into a feral wolf for 8s — +ATK, −dmg taken, Thornlash hits harder, but no healing spells',  effectType: 'bear_form',  iconKey: 'spell_bear_form' },
    ],
  },
  {
    heroClass: 'shaman', name: 'Zhorak', classLabel: 'Shaman', color: 0x38bdf8, hidden: true,
    description: 'Rotating-totem caster — one totem at a time, party buffs, and alternating spirit shields',
    spells: [
      { short: 'TOT', name: 'Totems',           desc: 'Rotates Lightning → Healing → Earthbind. Up to 3 on the board at once',                effectType: 'shaman_totems',      iconKey: 'spell_shaman_totems' },
      { short: 'CAL', name: 'Shamanic Call',    desc: 'Random party-wide on-hit buff: extra strike / fire damage / slow. 3-8 s',              effectType: 'shamanic_buff',      iconKey: 'spell_shamanic_call' },
      { short: 'SHD', name: 'Spirit Shield',    desc: 'Alternates Mana / Lightning shield, 3 stacks each, flat absorb',                       effectType: 'shaman_shield',      iconKey: 'spell_spirit_shield' },
      { short: 'ANC', name: 'Ancestral Guidance', desc: 'Empowers Zhorak\'s active totem — 2× tick rate, +50 % potency, refreshes duration',  effectType: 'ancestral_guidance', iconKey: 'spell_ancestral_guidance' },
    ],
  },
];

// Grid layout: 4 cards top row, 5 cards bottom row
const CARD_W = 176;
const CARD_H = 210;
const CARD_GAP = 8;

export class HeroSelectScene extends Phaser.Scene {
  private gsm!: GameStateManager;
  private selectedIndex = -1;
  private detailGroup!: Phaser.GameObjects.Group;
  private spellTooltip: Phaser.GameObjects.Container | Phaser.GameObjects.Text | null = null;
  private confirmBtnBg: Phaser.GameObjects.Rectangle | null = null;
  private confirmBtnTxt: Phaser.GameObjects.Text | null = null;

  constructor() {
    super({ key: 'HeroSelectScene' });
  }

  create(): void {
    setupCamera(this, this.cameras.main, 'full');
    this.gsm = GameStateManager.getInstance();
    this.cameras.main.setBackgroundColor('#0a0608');
    this.detailGroup = this.add.group();
    const width = GAME_W; const height = GAME_H;

    // Dark stone background texture
    this.add.rectangle(width / 2, height / 2, width, height, 0x0a0806, 1);
    // Subtle vignette top/bottom bars
    this.add.rectangle(width / 2, 0, width, 36, 0x0d0a08, 1).setOrigin(0.5, 0);
    this.add.rectangle(width / 2, 36, width, 1, 0x5a3600, 0.8).setOrigin(0.5, 0);
    this.add.rectangle(width / 2, height, width, 36, 0x0d0a08, 1).setOrigin(0.5, 1);
    this.add.rectangle(width / 2, height - 36, width, 1, 0x5a3600, 0.8).setOrigin(0.5, 1);

    // ESC → back to menu
    const canvas = this.game.canvas;
    canvas.setAttribute('tabindex', '1');
    canvas.focus();
    if (this.input.keyboard) {
      this.input.keyboard.once('keydown-ESC', () => this.scene.start('MenuScene'));
    }
    this.input.on('pointerdown', () => canvas.focus());

    // Back button — top left. Depth above the header occluder (99) so the button
    // stays visible and clickable when hero cards scroll up behind it.
    const backBg = this.add.rectangle(54, 18, 90, 28, 0x0d0a08, 1)
      .setStrokeStyle(1, 0x5a3600).setDepth(100).setInteractive({ useHandCursor: true });
    const backBtn = this.add.text(54, 18, '← Back', {
      fontSize: '11px', fontFamily: 'Georgia, serif', color: '#a07840',
    }).setOrigin(0.5).setDepth(101).setInteractive({ useHandCursor: true });
    const backOver = () => { backBtn.setColor('#c8973a'); backBg.setStrokeStyle(1, 0xc8973a); };
    const backOut  = () => { backBtn.setColor('#a07840'); backBg.setStrokeStyle(1, 0x5a3600); };
    const backDown = () => this.scene.start('MenuScene');
    backBg.on('pointerover', backOver).on('pointerout', backOut).on('pointerdown', backDown);
    backBtn.on('pointerover', backOver).on('pointerout', backOut).on('pointerdown', backDown);

    // Header occluder — solid bar that hides any hero card scrolled above the grid's
    // visible top. Mirrors the bottom btnBarBg pattern. Sits BELOW the title/subtitle
    // (depth 99) and ABOVE the grid container (depth 5).
    this.add.rectangle(width / 2, 30, width, 65, 0x0a0806, 1).setDepth(99);

    // Title — Phaser-drawn bronze filigree flanks the label so the header reads
    // as an ornamented heading instead of plain centred text. Approx text width
    // is derived from the font size × char count (22 px Georgia ≈ 12 px avg glyph).
    const titleText = 'Choose Your Hero';
    const titleLabel = this.add.text(width / 2, 18, titleText, {
      fontSize: '22px', fontFamily: 'Georgia, serif', color: '#c8973a', fontStyle: 'bold',
      shadow: { offsetX: 2, offsetY: 2, color: '#000000', blur: 4, fill: true },
    }).setOrigin(0.5).setDepth(100);
    drawTitleFlourish(this, width / 2, 18, titleLabel.width, 100);

    this.add.text(width / 2, 43, 'Select your champion. More heroes unlocked through quests and the armory.', {
      fontSize: '10px', fontFamily: 'Georgia, serif', color: '#5a3600',
    }).setOrigin(0.5).setDepth(100);

    // ── Hero card grid: 4 per row, wheel-scrolled if they overflow the viewport ──
    // Cards are CARD_W=176; 4 per row fits comfortably with generous side margins.
    const COLS = 4;
    const rowW = COLS * CARD_W + (COLS - 1) * CARD_GAP;
    const rowStartX = (width - rowW) / 2 + CARD_W / 2;
    const gridTopY = 175;
    const rowStrideY = CARD_H + CARD_GAP;

    // Container holds all cards — scrolled vertically by the wheel / drag.
    // Keep its depth well below the confirm button strip so sprites scrolling past the
    // bottom of the viewport never render on top of "Select & Begin".
    const gridContainer = this.add.container(0, 0).setDepth(5);
    const visibleHeroes = HERO_OPTIONS.filter((h) => !h.hidden);
    const totalRows = Math.ceil(visibleHeroes.length / COLS);
    visibleHeroes.forEach((opt, i) => {
      const col = i % COLS;
      const row = Math.floor(i / COLS);
      this.createHeroCard(rowStartX + col * (CARD_W + CARD_GAP), gridTopY + row * rowStrideY, opt, i, gridContainer);
    });

    // Scroll setup — allow the grid to drift vertically when content overflows.
    const visibleBottom = height - 80; // above confirm button strip
    const contentBottom = gridTopY + totalRows * rowStrideY - CARD_GAP;
    const maxScroll = Math.max(0, contentBottom - visibleBottom);
    let scrollY = 0;
    if (maxScroll > 0) {
      this.input.on('wheel', (_p: unknown, _o: unknown, _dx: number, dy: number) => {
        scrollY = Phaser.Math.Clamp(scrollY + dy * 0.6, 0, maxScroll);
        gridContainer.y = -scrollY;
      });
      // Scroll hint
      this.add.text(width - 20, gridTopY - 18, '↕ scroll', {
        fontSize: '9px', fontFamily: 'Georgia, serif', color: '#5a3600', fontStyle: 'italic',
      }).setResolution(2).setOrigin(1, 0);
    }

    // Confirm button — centered near bottom, solid background, full hit area
    const btnW = 220;
    const btnH = 38;
    const btnX = width / 2;
    const btnY = height - 26;  // center; button occupies btnY±19; stats strip at height-55

    // Behind the button: a solid bar that occludes any hero sprite scrolled past the
    // bottom of the card grid so sprites never show through the button strip.
    const btnBarBg = this.add.rectangle(btnX, btnY, width, btnH + 12, 0x0a0806, 1)
      .setDepth(99);
    void btnBarBg;

    this.confirmBtnBg = this.add.rectangle(btnX, btnY, btnW, btnH, 0x0d0a08, 1)
      .setStrokeStyle(1, 0x3a2800)
      .setName('confirmBtnBg')
      .setDepth(100)
      .setInteractive({ useHandCursor: true });

    this.confirmBtnTxt = this.add.text(btnX, btnY, 'Select & Begin', {
      fontSize: '14px', fontFamily: 'Georgia, serif', color: '#3a2800', fontStyle: 'bold',
    }).setOrigin(0.5).setName('confirmBtn')
      .setDepth(101)
      .setInteractive({ useHandCursor: true });

    const doConfirm = () => {
      if (this.selectedIndex < 0) return;
      const opt = HERO_OPTIONS.filter((h) => !h.hidden)[this.selectedIndex];
      this.gsm.pickStarterHero(opt.heroClass, opt.name, opt.heroKey);
      MenuScene.stopMenuMusic();
      // Loadout scene handles the "skip if no sigils in collection" case
      // itself — if the collection is empty it just commits an empty pick and
      // jumps straight to BattleScene, so we can always route through it here.
      this.scene.start('SigilLoadoutScene');
    };
    this.confirmBtnBg.on('pointerdown', doConfirm);
    this.confirmBtnTxt.on('pointerdown', doConfirm);
  }

  private activateConfirmButton(): void {
    if (!this.confirmBtnBg || !this.confirmBtnTxt) return;
    this.confirmBtnBg.setFillStyle(0x1a0608, 1).setStrokeStyle(2, 0x8b2000);
    this.confirmBtnTxt.setColor('#e8a030');

    const onOver = () => {
      this.confirmBtnTxt!.setColor('#ffe8a0');
      this.confirmBtnBg!.setStrokeStyle(2, 0xc84000);
    };
    const onOut = () => {
      this.confirmBtnTxt!.setColor('#e8a030');
      this.confirmBtnBg!.setStrokeStyle(2, 0x8b2000);
    };
    this.confirmBtnBg.off('pointerover').off('pointerout').on('pointerover', onOver).on('pointerout', onOut);
    this.confirmBtnTxt.off('pointerover').off('pointerout').on('pointerover', onOver).on('pointerout', onOut);
  }

  private cardBackgrounds: Phaser.GameObjects.Rectangle[] = [];
  /** Per-card flip state: `front` = items shown on the default face
   *  (portrait + description + spell icons), `back` = passive-preview panel
   *  items (created lazily the first time the card is flipped). */
  private cardFlipState: Map<number, {
    front: Phaser.GameObjects.GameObject[];
    back: Phaser.GameObjects.GameObject[];
    flipped: boolean;
  }> = new Map();

  private createHeroCard(x: number, cy: number, opt: HeroOption, index: number, parent?: Phaser.GameObjects.Container): void {
    const colorHex = `#${opt.color.toString(16).padStart(6, '0')}`;
    // Track which GameObjects belong to the card FRONT so we can hide them
    // when the card is flipped to show the passive preview.
    const frontItems: Phaser.GameObjects.GameObject[] = [];
    const add = <T extends Phaser.GameObjects.GameObject>(obj: T): T => {
      if (parent) parent.add(obj);
      return obj;
    };
    const addFront = <T extends Phaser.GameObjects.GameObject>(obj: T): T => {
      frontItems.push(obj);
      return add(obj);
    };

    // Armory progression info, used for the tiered card border AND the
    // Lv/XP readout below the name. Keys by heroKey first so amazon / dwarf
    // / necromancer variants pull their own account progression.
    const armoryInfo = this.gsm.getArmoryInfo(opt.heroKey ?? opt.heroClass);

    // Tiered border palette — every 10 Armory levels a new tier. The card
    // reads louder as you pour progression into that hero.
    //   Tier 1 (Lv 0-9):  dim bronze, single 1px border.
    //   Tier 2 (Lv 10-19): brighter bronze, 2px border.
    //   Tier 3 (Lv 20-30): gold, 2px border + inner accent ring.
    const tier = armoryInfo.level >= 20 ? 3 : armoryInfo.level >= 10 ? 2 : 1;
    const borderColor = tier === 3 ? 0xf0c040 : tier === 2 ? 0x8a6420 : 0x5a3600;
    const borderWidth = tier >= 2 ? 2 : 1;

    // Card background — dark stone. Stays visible on both sides.
    const bg = add(this.add.rectangle(x, cy, CARD_W, CARD_H, 0x0d0a08, 0.96));
    bg.setStrokeStyle(borderWidth, borderColor);
    bg.setData('armoryTier', tier);
    bg.setInteractive({ useHandCursor: true });
    this.cardBackgrounds.push(bg);

    // Tier 3 flourish: a slightly inset inner border in the same gold so the
    // top-tier cards read as distinct from tier 2 at a glance (drawn with
    // Phaser.Graphics — no emoji or asset dependency).
    if (tier === 3) {
      const accent = add(this.add.graphics());
      accent.lineStyle(1, 0xffe8a0, 0.75);
      accent.strokeRect(x - CARD_W / 2 + 4, cy - CARD_H / 2 + 4, CARD_W - 8, CARD_H - 8);
    }

    // Full 8-tier ornament on top — pips / gems / runes / laurels / shimmer
    // / halo / chromatic edge scale with Armory level beyond the old 3-tier
    // colour system. Tier 1 (gold corner pips) is the baseline for every hero
    // from Armory Lv 1 so cards never render with a bare frame.
    {
      const ornTier = Math.max(1, armoryFrameTier(armoryInfo.level));
      const result = drawHeroFrameOrnament(this, x, cy, CARD_W, CARD_H, ornTier, 6);
      for (const g of result.graphics) add(g);
    }

    // ── Name at top of card ──
    add(this.add.text(x, cy - CARD_H / 2 + 14, opt.name, {
      fontSize: '13px', fontFamily: 'Georgia, serif', color: colorHex, fontStyle: 'bold',
      shadow: { offsetX: 1, offsetY: 1, color: '#000', blur: 3, fill: true },
    }).setOrigin(0.5));

    // ── Class icon badge — top-right corner of the card ──
    // Source: drop matching PNGs into public/assets/ui/classicons/<class>.png
    // (suggested pack: https://www.svgrepo.com/collection/hearthsim-game-icons/).
    // Fallback is a class-colored tinted square so the slot stays visible until
    // the art is dropped in.
    // Prefer per-hero-key icon (morveth → necromancer, lootfi → dwarf, zayla → amazon);
    // fall back to base-class icon if the heroKey variant isn't registered.
    // Uses LOADED_ASSET_KEYS so we only show the badge when the REAL PNG loaded —
    // the fallback texture is a solid color square which looked like a placeholder.
    const iconKeyHero = `class_icon_${opt.heroKey ?? opt.heroClass}`;
    const iconKeyClass = `class_icon_${opt.heroClass}`;
    const iconKey = LOADED_ASSET_KEYS.has(iconKeyHero) ? iconKeyHero
                  : LOADED_ASSET_KEYS.has(iconKeyClass) ? iconKeyClass : null;
    if (iconKey) {
      add(this.add.image(x + CARD_W / 2 - 16, cy - CARD_H / 2 + 15, iconKey)
        .setDisplaySize(22, 22).setOrigin(0.5));
    }

    // ── Armory Lv + XP bar (replaces the class label — "class" terminology
    //    is being phased out player-side). Fits under the name strip, reads
    //    both the current level and the visible progress to the next tier. ──
    const lvY = cy - CARD_H / 2 + 30;
    const lvLabel = `Lv ${armoryInfo.level}`;
    const lvT = addFront(this.add.text(x - 46, lvY, lvLabel, {
      fontSize: '9px', fontFamily: 'Georgia, serif',
      color: tier === 3 ? '#ffe8a0' : tier === 2 ? '#c8973a' : '#8a6420',
      fontStyle: 'bold',
    }).setOrigin(1, 0.5));
    void lvT;
    const barW = 78;
    const barX = x - 44;
    const barH = 5;
    const barBg = addFront(this.add.rectangle(barX, lvY, barW, barH, 0x0d0a08, 1)
      .setOrigin(0, 0.5).setStrokeStyle(1, tier >= 2 ? 0x8a6420 : 0x5a3600));
    void barBg;
    if (!armoryInfo.atMax) {
      const pct = Math.min(1, armoryInfo.xpIntoLevel / Math.max(1, armoryInfo.xpToNext));
      if (pct > 0) {
        addFront(this.add.rectangle(barX + 1, lvY, (barW - 2) * pct, barH - 2, 0x6aaa60, 0.9)
          .setOrigin(0, 0.5));
      }
    } else {
      // Bar filled flat gold at max level.
      addFront(this.add.rectangle(barX + 1, lvY, barW - 2, barH - 2, 0xffe8a0, 0.85)
        .setOrigin(0, 0.5));
    }

    // ── Hero portrait — centered, larger (front-only) ──
    const portraitY = cy - 14;
    const hk = opt.heroKey ?? opt.heroClass;
    const portrait = addFront(this.add.sprite(x, portraitY + 10, `hero_${hk}`)
      .setDisplaySize(106, 106).setOrigin(0.5, 1.0));
    const idleKey = `hero_${hk}_idle`;
    if (this.anims.exists(idleKey)) portrait.play(idleKey);

    // Ground shadow (front-only). Thorne / Morveth had a -6 lift left over
    // from the old Pixellab sprites — both are on craftpix sheets now whose
    // feet sit at the frame bottom, so they no longer need lifting (matches
    // the in-battle BattleScene shadow offsets after that earlier fix).
    const shadowYOffset: Record<string, number> = { shaman: -6 };
    addFront(this.add.ellipse(x, portraitY + 12 + (shadowYOffset[hk] ?? 0), 44, 5, 0x000000, 0.82));

    // ── Short description (front-only) ──
    addFront(this.add.text(x, cy + 28, opt.description, {
      fontSize: '9px', fontFamily: 'Georgia, serif', color: '#6b4020',
      wordWrap: { width: CARD_W - 20 }, align: 'center',
    }).setOrigin(0.5));

    // ── Spell icons row ──
    const iconSize = 30;
    const iconGap = 5;
    const totalIconW = opt.spells.length * iconSize + (opt.spells.length - 1) * iconGap;
    const iconStartX = x - totalIconW / 2 + iconSize / 2;
    const iconY = cy + CARD_H / 2 - iconSize / 2 - 8;

    opt.spells.forEach((spell, si) => {
      const ix = iconStartX + si * (iconSize + iconGap);
      const iconColor = ABILITY_COLORS[spell.effectType] ?? 0x6b7280;
      const hasSprite = spell.iconKey && this.textures.exists(spell.iconKey);

      const iconBg = addFront(this.add.rectangle(ix, iconY, iconSize, iconSize, 0x0d0a08, 0.95)
        .setStrokeStyle(1, iconColor, 0.7)
        .setInteractive({ useHandCursor: true }));

      if (hasSprite) {
        addFront(this.add.image(ix, iconY, spell.iconKey!).setDisplaySize(iconSize - 6, iconSize - 6));
      } else {
        addFront(this.add.text(ix, iconY, spell.short, {
          fontSize: '9px', fontFamily: 'Georgia, serif', color: colorHex, fontStyle: 'bold',
        }).setOrigin(0.5));
      }

      const showTip = () => {
        this.spellTooltip?.destroy();
        const isPassive = spell.effectType === 'passive_resurrect' || spell.effectType === 'passive_loot';
        const tipTitle = isPassive ? `${spell.name} (Passive)` : spell.name;
        // Tooltip stays at scene level (above scrolling container) so it's always readable
        this.spellTooltip = createHudTooltip(this, ix, (parent ? parent.y : 0) + iconY - 14,
          `${tipTitle}\n${spell.desc}`,
          0.5, 1, { fontSize: '10px', wrap: 170, depth: 60 });
      };
      const hideTip = () => { this.spellTooltip?.destroy(); this.spellTooltip = null; };
      iconBg.on('pointerover', showTip).on('pointerout', hideTip);
    });

    // ── Card interaction ──
    bg.on('pointerover', () => {
      if (this.selectedIndex !== index) bg.setFillStyle(0x13100a, 0.98);
    });
    bg.on('pointerout', () => {
      if (this.selectedIndex !== index) bg.setFillStyle(0x0d0a08, 0.96);
    });
    bg.on('pointerdown', () => {
      this.selectedIndex = index;
      // Reset all known card backgrounds to their own tier stroke/width, then
      // highlight the picked one with a hero-color border. Each bg carries
      // its own armory tier so we can't just stamp a single colour across
      // the list during the reset pass — read back via `getData`.
      for (const cb of this.cardBackgrounds) {
        const cbTier = (cb.getData('armoryTier') as number | undefined) ?? 1;
        const cbColor = cbTier === 3 ? 0xf0c040 : cbTier === 2 ? 0x8a6420 : 0x5a3600;
        cb.setStrokeStyle(cbTier >= 2 ? 2 : 1, cbColor);
        cb.setFillStyle(0x0d0a08, 0.96);
      }
      bg.setStrokeStyle(2, opt.color);
      bg.setFillStyle(0x13100a, 0.98);

      this.activateConfirmButton();
      this.showDetail(opt);
    });

    // ── Flip button (top-LEFT corner) — Phaser-drawn bronze chevron arrows,
    //    never an emoji. Clicking toggles the card between its default face
    //    (portrait + description + spell icons) and a passive-preview face.
    const flipBtnX = x - CARD_W / 2 + 14;
    const flipBtnY = cy - CARD_H / 2 + 14;
    const flipG = add(this.add.graphics().setDepth(5));
    const drawFlipGlyph = (color: number) => {
      flipG.clear();
      flipG.fillStyle(0x0d0a08, 0.95);
      flipG.lineStyle(1, color, 0.95);
      flipG.fillCircle(flipBtnX, flipBtnY, 10);
      flipG.strokeCircle(flipBtnX, flipBtnY, 10);
      flipG.fillStyle(color, 1);
      // Two back-to-back chevrons reading as "flip / rotate".
      flipG.fillTriangle(flipBtnX - 4, flipBtnY - 3, flipBtnX - 1, flipBtnY - 3, flipBtnX - 2.5, flipBtnY);
      flipG.fillTriangle(flipBtnX + 4, flipBtnY + 3, flipBtnX + 1, flipBtnY + 3, flipBtnX + 2.5, flipBtnY);
    };
    drawFlipGlyph(0xc8973a);
    const flipHitArea = add(this.add.rectangle(flipBtnX, flipBtnY, 22, 22, 0x000000, 0)
      .setInteractive({ useHandCursor: true }));
    flipHitArea.on('pointerover', () => drawFlipGlyph(0xffe8a0));
    flipHitArea.on('pointerout',  () => drawFlipGlyph(0xc8973a));
    flipHitArea.on('pointerdown', () => this.toggleCardFlip(index, opt, x, cy, parent));

    this.cardFlipState.set(index, { front: frontItems, back: [], flipped: false });
  }

  /** Flip a hero card between default face (portrait + spells) and the
   *  passive-preview face. First flip lazily builds the back-side content. */
  private toggleCardFlip(
    index: number,
    opt: HeroOption,
    x: number,
    cy: number,
    parent?: Phaser.GameObjects.Container,
  ): void {
    const state = this.cardFlipState.get(index);
    if (!state) return;
    const addParented = <T extends Phaser.GameObjects.GameObject>(obj: T): T => {
      if (parent) parent.add(obj);
      return obj;
    };
    if (state.flipped) {
      // Flip back → destroy back objects, show front again.
      for (const o of state.back) o.destroy();
      state.back = [];
      for (const f of state.front) (f as Phaser.GameObjects.Image).setVisible(true);
      state.flipped = false;
      return;
    }
    // Flip to back — hide front items + build the perk preview. Mirrors the
    // front-side spell-icons pattern: a header at top, a row of rune-icon
    // tiles below, hover any tile to see name + description in a tooltip.
    // Avoids the old stacked-text-rows overlap shown in the playtest screenshot.
    for (const f of state.front) (f as Phaser.GameObjects.Image).setVisible(false);
    const passives = getPassivesForHero(opt.heroClass, opt.heroKey);
    const headerY = cy - CARD_H / 2 + 50;
    const header = addParented(this.add.text(x, headerY, 'ELITE PERKS', {
      fontSize: '12px', fontFamily: "'Cinzel Decorative', Georgia, serif",
      color: '#c8973a', fontStyle: 'bold',
    }).setOrigin(0.5));
    state.back.push(header);
    if (passives.length === 0) {
      const empty = addParented(this.add.text(x, cy, 'No perks authored yet', {
        fontSize: '10px', fontFamily: 'Georgia, serif', color: '#5a3600', fontStyle: 'italic',
      }).setOrigin(0.5));
      state.back.push(empty);
      state.flipped = true;
      return;
    }
    // Tile grid: max 4 per row so 5+ perks wrap to a second row instead of
    // spilling past the card edge. Card is 176 px wide; 4 × 36 + 3 × 6 = 162 px
    // — leaves 7 px of margin on each side.
    const tileSize = 36;
    const tileGap = 6;
    const cols = 4;
    const visiblePerks = passives.slice(0, 8); // hard cap at 8 (2 full rows)
    const tileY0 = headerY + 36;
    visiblePerks.forEach((def, i) => {
      const col = i % cols;
      const row = Math.floor(i / cols);
      // Centre each row independently so a partial bottom row stays centred.
      const rowItems = Math.min(cols, visiblePerks.length - row * cols);
      const rowW = rowItems * tileSize + (rowItems - 1) * tileGap;
      const rowStartX = x - rowW / 2 + tileSize / 2;
      const ix = rowStartX + col * (tileSize + tileGap);
      const tileY = tileY0 + row * (tileSize + tileGap);
      const tileBg = addParented(this.add.rectangle(ix, tileY, tileSize, tileSize, 0x0d0a08, 0.95)
        .setStrokeStyle(1, 0xc8973a, 0.85).setInteractive({ useHandCursor: true }));
      const icon = addParented(this.add.text(ix, tileY, def.icon, {
        fontSize: '20px', fontFamily: "'Cinzel Decorative', Georgia, serif",
        color: '#e8c060', fontStyle: 'bold',
      }).setOrigin(0.5));
      const showTip = () => {
        this.spellTooltip?.destroy();
        this.spellTooltip = createHudTooltip(this, ix, (parent ? parent.y : 0) + tileY - 22,
          `${def.name}\n${def.description}`,
          0.5, 1, { fontSize: '10px', wrap: 200, depth: 60 });
        tileBg.setStrokeStyle(1, 0xffe8a0, 1);
      };
      const hideTip = () => {
        this.spellTooltip?.destroy();
        this.spellTooltip = null;
        tileBg.setStrokeStyle(1, 0xc8973a, 0.85);
      };
      tileBg.on('pointerover', showTip).on('pointerout', hideTip);
      state.back.push(tileBg, icon);
    });
    state.flipped = true;
  }

  private showDetail(_opt: HeroOption): void {
    // Bottom stat strip was removed — it frequently overlapped the row-2 cards
    // at the hero portrait's feet + the "Select & Begin" button strip depending
    // on scroll position and card layout, so it read as visual noise rather
    // than useful info. Hero stats still live in-scene inside each card's
    // passive-preview flip view and on the Hero menu after selection, so the
    // data is not lost — just no longer duplicated at the bottom of the
    // selection screen. Kept this method as a no-op so the click handler at
    // line ~532 still has a stable call site, and so any lingering detailGroup
    // children from a previous render (pre-deploy) get cleared.
    this.detailGroup.getChildren().slice().forEach((c) => c.destroy());
    this.detailGroup.clear(true, true);
  }
}
